import { uniqueId } from 'lodash';
import moment from 'moment';
import { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDashboardData } from '../../../api/dashboard';
import { useSetLoading } from '../../../common/hooks/appearance';
import { reportTypes } from '../../../constants/reports';
import { routes } from '../../../constants/routes';
import { dateFormat, fetchDashboardFilterData } from '../../../utils/constant';
import { apiErrorHandler } from '../../../utils/handlers';
import ARAgingGraph from '../graph-cards/AR-Aging';
import ARSegmentBreakdown from '../graph-cards/AR-seg-breakdown';
import IndividualDashboardLayout from '../IndividualDashboardLayout';
import ReceivablesCard from '../overview-cards/receivables-card';
import { DASHBOARD_COLORS, DURATION, RECEIVABLES_STRUCTURE } from '../utils/dashboardConstants';

function ReceivablesDashboard() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ ...RECEIVABLES_STRUCTURE });
  const [reportDate, setReportDate] = useState(moment());
  const [contractNumber, setContractNumber] = useState('');
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const filtersReady = filterData?.entity?.length > 0;
  const segmentPieId = useMemo(() => uniqueId('ar-aging-segmentation-pie-id'), []);
  const agingBucketsGraphId = useMemo(() => uniqueId('ar-aging-buckets-graph-id'), []);

  const getDashboardHome = async () => {
    setLoading(true);
    try {
      const dataAvailable = fetchDashboardFilterData(filterData);
      const reqParams = {
        ...dataAvailable,
        opiOrName: contractNumber,
        reportDate: moment(reportDate).format(dateFormat.YEAR_MONTH_DAY),
        timezone: moment.tz.guess(),
      };
      const { data: responseData } = await getDashboardData('dashboard', 'receivables', reqParams);
      setData(responseData.data.items);
    } catch (error) {
      setLoading(false);
      apiErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filtersReady) getDashboardHome();
  }, [filtersReady, filterData, reportDate, contractNumber]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const redirectionHandler = (query) => {
    const updatedQuery = { ...query };
    updatedQuery.reportDate = moment(reportDate).format(dateFormat.YEAR_MONTH_DAY);
    updatedQuery.searchText = contractNumber;
    history.push({ pathname: query.pathname, state: updatedQuery });
  };

  const dateChangeHandler = (date) => setReportDate(date);
  const searchHandler = (searchedValue) => setContractNumber(searchedValue);

  const getReportTypeValues = (reportType) => {
    return reportTypes.find((report) => report.value === reportType);
  };

  return (
    <IndividualDashboardLayout
      durationType={DURATION.SINGLE}
      dateChangeHandler={dateChangeHandler}
      searchHandler={searchHandler}
    >
      <div className="dashboard-row-container">
        <ReceivablesCard
          data={{
            amount: data.receivables.totalAmount,
            outstandingAmount: data.receivables.outstandingAmount,
            outstandingAmountPercent: data.receivables.outstandingAmountPercent,
            overDueAmount: data.receivables.overDueAmount,
            overDueAmountPercent: data.receivables.overDueAmountPercent,
            date: data.lastUpdatedAt || new Date(),
            redirection: {
              enabled: true,
              path: routes.REPORTS,
              filterType: 'reportType',
              redirect: redirectionHandler,
              values: getReportTypeValues('arAgingDetailedReport'),
            },
          }}
          bgColor={DASHBOARD_COLORS.RECEIVABLES.HEADER}
        />
        <ARSegmentBreakdown
          id={segmentPieId}
          data={{
            graphType: 'pie',
            title: 'Accounts Receivable (Segment Breakdown)',
            tooltip:
              'This chart will show the amount of Total Receivables divided into different Contract Type',
            segmentation: data?.accountsReceivablesAging?.segmentation?.map((mode) => {
              return {
                serviceType: mode.serviceType,
                totalAmount: mode.unDue + mode.overDue,
              };
            }),
            totalAmount:
              data?.accountsReceivablesAging?.unDue + data?.accountsReceivablesAging?.overDue,
            redirection: {
              enabled: true,
              path: routes.REPORTS,
              filterType: 'reportType',
              redirect: redirectionHandler,
              values: getReportTypeValues('arAgingDetailedReport'),
            },
          }}
        />
      </div>
      <div className="ar-aging-buckets-graph">
        <ARAgingGraph
          id={agingBucketsGraphId}
          data={{
            graphType: 'bar',
            title: 'Accounts Receivable Aging',
            tooltip:
              'This chart will show the amount of Total Receivables divided into different Age Buckets',
            graphColor: DASHBOARD_COLORS.AR_AGING,
            showDataLabels: true,
            graphTitle: {
              x: 'AGING IN DAYS',
              y: 'AMOUNT',
            },
            impNote: '',
            series: [
              {
                name: 'Amount',
                type: 'column',
                data: data.accountsReceivablesAging.seriesData.includes(null)
                  ? []
                  : data.accountsReceivablesAging.seriesData,
              },
            ],
            xLabels: ['0 - 30', '31 - 60', '61 - 90', '91 - 120', '> 120'],
            redirection: {
              enabled: true,
              path: routes.REPORTS,
              filterType: 'reportType',
              redirect: redirectionHandler,
              values: getReportTypeValues('arAgingDetailedReport'),
            },
          }}
        />
      </div>
    </IndividualDashboardLayout>
  );
}

export default memo(ReceivablesDashboard);
